import React from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { AlertCircle, BarChart3 } from "lucide-react";

interface SignalStrategySettingsProps {
  botData: any;
  updateSettings: (key: string, value: any) => void;
  updateMultiSettings: (updates: Record<string, any>) => void;
}

export const SignalStrategySettings: React.FC<SignalStrategySettingsProps> = ({
  botData,
  updateSettings,
  updateMultiSettings
}) => {
  const { strategy_type, symbol, settings } = botData;

  const intervalMap: Record<string, string> = {
    '5m': '15m', '15m': '30m', '30m': '1h', '1h': '4h', '4h': '12h'
  };
  const derivedHigherInterval = intervalMap[settings.interval || '15m'] || '30m';

  return (
    <div className="space-y-4">
      {strategy_type === 'hybrid_pullback' && (
        <div className="space-y-4">
          <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-2xl">
            <h4 className="font-black text-purple-600 text-sm mb-2">💡 Hybrid Pullback 전략 특징</h4>
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1 font-bold">
              <li>1시간봉 Stochastic 과매도(K/D상승) 및 과매수 필터링을 통해 대세 추세를 진단합니다.</li>
              <li>30분봉 BB_ICHI 구름대 색상 및 EMA 120 방향을 통해 미세 추세 필터링을 적용합니다.</li>
              <li>15분봉 Stochastic (20,10,10) 크로스(골든/데드) 및 판매선(panmae) 5봉 내 근접 도달 시 진입 타점을 잡습니다.</li>
              <li>ADX(14) 20 이상 구간에서 필터링을 통해 횡보장을 최대한 필터링합니다.</li>
            </ul>
          </div>
        </div>
      )}

      {strategy_type === 'super_hybrid' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">KEWMA Length</Label>
              <Input
                type="number"
                value={settings.kewma_length || 45}
                onChange={(e) => updateSettings('kewma_length', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">ST Period</Label>
              <Input
                type="number"
                value={settings.st_period || 10}
                onChange={(e) => updateSettings('st_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">ST Multiplier</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.st_multiplier || 3.0}
                onChange={(e) => updateSettings('st_multiplier', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Cluster Min Bars</Label>
              <Input
                type="number"
                min={0}
                max={20}
                step={1}
                value={settings.cluster_min_bars ?? 3}
                onChange={(e) => updateSettings('cluster_min_bars', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[8px] text-muted-foreground font-medium px-0.5">0: 비활성화 | N: KEWMA+ST 동일방향 N봉 유지 시 진입</p>
            </div>
            </div>
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-[11px] text-blue-400 font-bold">
              💡 Super Hybrid 전략은 4시간봉 슈퍼 이평선 점수(Macro)와 15분봉 KEWMA + SuperTrend(Micro)를 결합하여 추세가 일치할 때만 진입합니다.
            </p>
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>매크로 점수 기반 포지션 크기 조절</Label>
                <p className="text-[10px] text-muted-foreground">점수가 약하면(3점) 60%, 강하면(5점) 100% 진입</p>
              </div>
              <Checkbox
                checked={settings.use_size_by_macro_score !== false}
                onCheckedChange={(checked) => updateSettings('use_size_by_macro_score', checked)}
              />
            </div>
          </div>
        </div>
      )}

      {strategy_type === 'channel_trend' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">LR Period (Channel)</Label>
              <Input
                type="number"
                value={settings.lr_period || 50}
                onChange={(e) => updateSettings('lr_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-background/50">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">RSI Filter</Label>
                <p className="text-[10px] text-muted-foreground">과매수/과매도 필터 사용</p>
              </div>
              <Checkbox
                checked={settings.use_rsi_filter || false}
                onCheckedChange={(checked) => updateSettings('use_rsi_filter', checked)}
              />
            </div>
            <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">EMA Filter</Label>
                  <p className="text-[10px] text-muted-foreground">장기 이평선 방향 필터</p>
                </div>
                <Checkbox
                  checked={settings.use_ema_filter || false}
                  onCheckedChange={(checked) => updateSettings('use_ema_filter', checked)}
                />
              </div>
              {settings.use_ema_filter && (
                <div className="flex items-center gap-2 pt-1 border-t border-border/50">
                  <span className="text-[9px] font-bold text-muted-foreground">Period:</span>
                  <Input
                    type="number"
                    value={settings.ema_period || 200}
                    onChange={(e) => updateSettings('ema_period', Number(e.target.value))}
                    className="h-6 w-32 text-[9px] bg-background/50"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">Daily Overheat Guard</Label>
                <p className="text-[10px] text-muted-foreground">오늘 상승/하락이 너무 크면 진입 차단</p>
              </div>
              <Checkbox
                checked={settings.use_uptrend_daily_guard !== false}
                onCheckedChange={(checked) => updateSettings('use_uptrend_daily_guard', checked)}
              />
            </div>
            {settings.use_uptrend_daily_guard !== false && (
              <div className="flex items-center justify-between p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">Daily Threshold</Label>
                  <p className="text-[10px] text-muted-foreground">일간 변동 허용치 (%)</p>
                </div>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.uptrend_daily_gain_pct ?? 5.0}
                  onChange={(e) => updateSettings('uptrend_daily_gain_pct', Number(e.target.value))}
                  className="h-8 w-24 text-[10px] bg-background/50"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">4h Overheat Guard</Label>
                <p className="text-[10px] text-muted-foreground">4시간 변동이 너무 크면 진입 차단</p>
              </div>
              <Checkbox
                checked={settings.use_uptrend_4h_guard !== false}
                onCheckedChange={(checked) => updateSettings('use_uptrend_4h_guard', checked)}
              />
            </div>
            {settings.use_uptrend_4h_guard !== false && (
              <div className="grid grid-cols-2 gap-2 p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
                <div className="space-y-0.5">
                  <Label className="text-[10px] font-bold">4h Threshold</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.uptrend_gain_pct_4h ?? 2.5}
                    onChange={(e) => updateSettings('uptrend_gain_pct_4h', Number(e.target.value))}
                    className="h-8 text-[10px] bg-background/50"
                  />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[10px] font-bold">4h ATR x</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.uptrend_atr_multiple_4h ?? 1.5}
                    onChange={(e) => updateSettings('uptrend_atr_multiple_4h', Number(e.target.value))}
                    className="h-8 text-[10px] bg-background/50"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-primary" />
                  1시간봉 ADX 필터 (Macro Trend Filter)
                </Label>
                <p className="text-[10px] text-muted-foreground">1시간봉 기준 추세가 너무 강하면 진입을 제한합니다.</p>
              </div>
              <Checkbox
                checked={settings.use_adx_filter || false}
                onCheckedChange={(checked) => updateSettings('use_adx_filter', checked)}
              />
            </div>
            {settings.use_adx_filter && (
              <div className="space-y-3 pt-2 border-t border-border/50">
                <div className="flex justify-between items-center px-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase">ADX 진입 제한 임계치</span>
                  <span className="text-xs font-black text-primary">{settings.adx_threshold || 25} {settings.adx_mode === 'trend' ? '이상' : '미만'}일 때만</span>
                </div>
                <Slider
                  min={15}
                  max={50}
                  step={1}
                  value={[settings.adx_threshold || 25]}
                  onValueChange={(vals) => updateSettings('adx_threshold', vals[0])}
                />

                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant={settings.adx_mode !== 'trend' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateSettings('adx_mode', 'sideways')}
                    className="flex-1 h-7 text-[10px] font-black rounded-lg"
                  >
                    횡보장 전용 (ADX 미만)
                  </Button>
                  <Button
                    type="button"
                    variant={settings.adx_mode === 'trend' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateSettings('adx_mode', 'trend')}
                    className="flex-1 h-7 text-[10px] font-black rounded-lg"
                  >
                    추세장 전용 (ADX 이상)
                  </Button>
                </div>

                <p className="text-[9px] text-muted-foreground leading-relaxed italic">
                  * 채널 반등(Rebound) 시 권장: ADX 20~25 미만 (횡보장 전용)
                </p>
              </div>
            )}
          </div>

          <div className="p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />
                  상위 TF ZL-MACD 방향 필터 (HTF Alignment)
                </Label>
                <p className="text-[10px] text-muted-foreground">채널 방향 + 상위 TF ZL-MACD 방향 일치 시만 진입 (롱: MACD&gt;Signal, 숏: MACD{'<'}Signal)</p>
              </div>
              <Checkbox
                checked={settings.use_htf_zl_macd_filter !== false}
                onCheckedChange={(checked) => updateSettings('use_htf_zl_macd_filter', checked)}
              />
            </div>
            {settings.use_htf_zl_macd_filter !== false && (
              <div className="grid grid-cols-4 gap-2 pt-2 border-t border-emerald-500/20">
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">상위 TF</span>
                  <Select
                    value={settings.htf_tf || '1h'}
                    onValueChange={(v) => updateSettings('htf_tf', v)}
                  >
                    <SelectTrigger className="h-7 text-[9px] font-bold bg-background/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="30m">30분</SelectItem>
                      <SelectItem value="1h">1시간</SelectItem>
                      <SelectItem value="2h">2시간</SelectItem>
                      <SelectItem value="4h">4시간</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">Fast</span>
                  <Input
                    type="number"
                    value={settings.htf_zl_macd_fast || 12}
                    onChange={(e) => updateSettings('htf_zl_macd_fast', Number(e.target.value))}
                    className="h-7 text-[9px] font-bold bg-background/50"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">Slow</span>
                  <Input
                    type="number"
                    value={settings.htf_zl_macd_slow || 26}
                    onChange={(e) => updateSettings('htf_zl_macd_slow', Number(e.target.value))}
                    className="h-7 text-[9px] font-bold bg-background/50"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">Signal</span>
                  <Input
                    type="number"
                    value={settings.htf_zl_macd_sig || 9}
                    onChange={(e) => updateSettings('htf_zl_macd_sig', Number(e.target.value))}
                    className="h-7 text-[9px] font-bold bg-background/50"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between px-1">
              <Label className="text-[10px] font-black text-muted-foreground uppercase">Boundary Requirement (진입 영역)</Label>
              <span className="text-[10px] font-bold text-primary">{settings.boundary_pct || 20}% 영역</span>
            </div>
            <p className="text-[9px] text-muted-foreground px-1 pb-1">
              채널 중심선과 하단선 사이의 하위 구간 중, 바닥권에서 얼마나 가까울 때 진입할지 결정합니다. 수치가 작을수록 더 완벽한 바닥을 노립니다.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => {
                  updateSettings('boundary_mode', 'none');
                  updateSettings('boundary_pct', 100);
                }}
                className={`h-9 text-xs font-bold rounded-lg border transition-all ${(settings.boundary_mode || 'none') === 'none' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                제한 없음
              </button>
              <button
                type="button"
                onClick={() => {
                  updateSettings('boundary_mode', 'half');
                  updateSettings('boundary_pct', 50);
                }}
                className={`h-9 text-xs font-bold rounded-lg border transition-all ${settings.boundary_mode === 'half' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                하단/상단 50%
              </button>
              <button
                type="button"
                onClick={() => {
                  updateSettings('boundary_mode', 'edge');
                  updateSettings('boundary_pct', 40);
                }}
                className={`h-9 text-xs font-bold rounded-lg border transition-all ${settings.boundary_mode === 'edge' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                경계선 영역 (이니시)
              </button>
            </div>
            {settings.boundary_mode === 'edge' && (
              <div className="px-1 pt-1 space-y-4">
                <Slider
                  min={5}
                  max={50}
                  step={5}
                  value={[settings.boundary_pct || 20]}
                  onValueChange={(vals) => updateSettings('boundary_pct', vals[0])}
                  className="py-2"
                />
                <div className="flex justify-between">
                  <span className="text-[8px] text-muted-foreground font-bold">좁게 (5%)</span>
                  <span className="text-[8px] text-muted-foreground font-bold text-primary">현재: {settings.boundary_pct || 20}%</span>
                  <span className="text-[8px] text-muted-foreground font-bold">넓게 (50%)</span>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">중심선(Midline) 익절 사용</Label>
                  <p className="text-[10px] text-muted-foreground">채널 중앙선 터치 시 포지션 종료</p>
                </div>
                <Checkbox
                  checked={settings.use_midline_tp !== false}
                  onCheckedChange={(checked) => updateSettings('use_midline_tp', checked)}
                />
              </div>

              {settings.use_midline_tp !== false && (
                <div className="space-y-2 pt-2 border-t border-border/50">
                  <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Min Profit to Exit (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.min_profit_pct || 0.5}
                    onChange={(e) => updateSettings('min_profit_pct', Number(e.target.value))}
                    className="h-9 text-xs font-bold border-border rounded-lg bg-background"
                  />
                  <p className="text-[9px] text-muted-foreground px-1 leading-relaxed">
                    채널 중심선 도달 시 익절하기 위한 최소 수익률입니다. <br />
                    이 수치보다 낮으면 중심선에 닿아도 팔지 않고 더 큰 반등을 기다립니다.
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">Trailing Stop</Label>
                  <p className="text-[10px] text-muted-foreground">수익 극대화 트레일링</p>
                </div>
                <Checkbox
                  checked={settings.use_trailing_stop || false}
                  onCheckedChange={(checked) => updateSettings('use_trailing_stop', checked)}
                />
              </div>
              <p className="text-[9px] text-muted-foreground px-1">수익이 발생하면 고점을 추격하며 익절가를 자동으로 높여 수익을 극대화합니다.</p>
              {settings.use_trailing_stop && (
                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/50">
                  <div className="space-y-1">
                    <span className="text-[8px] font-bold text-muted-foreground">Activation (%)</span>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.trailing_activation_pct || 1.5}
                      onChange={(e) => updateSettings('trailing_activation_pct', Number(e.target.value))}
                      className="h-7 text-[10px] font-bold bg-background/50"
                    />
                  </div>
                  <div className="space-y-1">
                    <span className="text-[8px] font-bold text-muted-foreground">Callback (%)</span>
                    <Input
                      type="number"
                      step="0.1"
                      value={settings.trailing_callback_pct || 0.5}
                      onChange={(e) => updateSettings('trailing_callback_pct', Number(e.target.value))}
                      className="h-7 text-[10px] font-bold bg-background/50"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col p-3 border border-red-500/30 rounded-lg bg-red-500/5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                  채널 이탈 손절 (Breakout SL)
                </Label>
                <p className="text-[10px] text-muted-foreground">채널 상/하단 이탈 시 즉시 손절합니다</p>
              </div>
              <Checkbox
                checked={settings.use_channel_breakout_sl !== false}
                onCheckedChange={(checked) => updateSettings('use_channel_breakout_sl', checked)}
              />
            </div>
            {settings.use_channel_breakout_sl !== false && (
              <div className="space-y-3 pt-2 border-t border-red-500/20">
                <div className="flex justify-between items-center px-1">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase">이탈 허용 폭</span>
                  <span className="text-xs font-black text-red-400">{settings.channel_breakout_sl_pct || 1.0}%</span>
                </div>
                <Slider
                  min={0.1}
                  max={3.0}
                  step={0.1}
                  value={[settings.channel_breakout_sl_pct || 1.0]}
                  onValueChange={(vals) => updateSettings('channel_breakout_sl_pct', vals[0])}
                />
                <div className="flex justify-between">
                  <span className="text-[8px] text-muted-foreground">타이트 (0.1%)</span>
                  <span className="text-[8px] text-muted-foreground">권장: 1.0%</span>
                  <span className="text-[8px] text-muted-foreground">느슨 (3.0%)</span>
                </div>
                <p className="text-[9px] text-muted-foreground/80 leading-relaxed italic">
                  * 백테스트 결과 1.0%가 최적 (+12.36%, MDD 36.12%). 0.5% 이하로 설정 시 잦은 손절로 수익률이 저하됩니다.
                </p>
              </div>
            )}
          </div>

          <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <p className="text-[11px] text-purple-400 font-bold">
              💡 Channel Trend 전략은 선형 회귀 채널(Linear Regression)의 방향으로 추세를 식별하고, 채널 경계 터치 시 반등/반락 진입합니다. 상위 TF ZL-MACD 방향 필터(1h)로 채널 방향과 추세 방향이 일치할 때만 진입하여 리페인팅으로 인한 허위 신호를 차단합니다.
            </p>
          </div>
        </div>
      )}

      {strategy_type === 'channel_trend2' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">LR Period</Label>
              <Input
                type="number"
                value={settings.lr_period || 100}
                onChange={(e) => updateSettings('lr_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">EMA Period</Label>
              <Input
                type="number"
                value={settings.ema_period || 200}
                onChange={(e) => updateSettings('ema_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Fast EMA Period</Label>
              <Input
                type="number"
                value={settings.fast_ema_period || 9}
                onChange={(e) => updateSettings('fast_ema_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">EMA Angle Period</Label>
              <Input
                type="number"
                value={settings.ema_angle_period || 27}
                onChange={(e) => updateSettings('ema_angle_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Angle Threshold (임계치)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.angle_threshold || -1.0}
                onChange={(e) => updateSettings('angle_threshold', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Channel Interval (초)</Label>
              <Select
                value={String(settings.channel_interval_sec || 3600)}
                onValueChange={(v) => updateSettings('channel_interval_sec', Number(v))}
              >
                <SelectTrigger className="h-9 text-xs font-bold border-border rounded-lg bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="1800">30분 (1800s)</SelectItem>
                  <SelectItem value="3600">1시간 (3600s)</SelectItem>
                  <SelectItem value="7200">2시간 (7200s)</SelectItem>
                  <SelectItem value="14400">4시간 (14400s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Filter Type (진입 필터)</Label>
              <Select
                value={settings.filter_type || 'ema_cross_angle'}
                onValueChange={(v) => updateSettings('filter_type', v)}
              >
                <SelectTrigger className="h-9 text-xs font-bold border-border rounded-lg bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="ema_cross_angle">EMA Cross Angle (1안)</SelectItem>
                  <SelectItem value="double_bottom">Double Bottom (2안)</SelectItem>
                  <SelectItem value="legacy">Legacy Filter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-background/50">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">Midline TP (중심선 익절)</Label>
                <p className="text-[10px] text-muted-foreground">채널 중앙선 터치 시 분할/전체 익절</p>
              </div>
              <Checkbox
                checked={settings.use_midline_tp !== false}
                onCheckedChange={(checked) => updateSettings('use_midline_tp', checked)}
              />
            </div>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-background/50">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">Trailing Stop</Label>
                <p className="text-[10px] text-muted-foreground">익절 고점 추적</p>
              </div>
              <Checkbox
                checked={settings.use_trailing_stop !== false}
                onCheckedChange={(checked) => updateSettings('use_trailing_stop', checked)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">Daily Overheat Guard</Label>
                <p className="text-[10px] text-muted-foreground">오늘 상승/하락이 너무 크면 진입 차단</p>
              </div>
              <Checkbox
                checked={settings.use_uptrend_daily_guard !== false}
                onCheckedChange={(checked) => updateSettings('use_uptrend_daily_guard', checked)}
              />
            </div>
            {settings.use_uptrend_daily_guard !== false && (
              <div className="flex items-center justify-between p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">Daily Threshold</Label>
                  <p className="text-[10px] text-muted-foreground">일간 변동 허용치 (%)</p>
                </div>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.uptrend_daily_gain_pct ?? 5.0}
                  onChange={(e) => updateSettings('uptrend_daily_gain_pct', Number(e.target.value))}
                  className="h-8 w-24 text-[10px] bg-background/50"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">4h Overheat Guard</Label>
                <p className="text-[10px] text-muted-foreground">4시간 변동이 너무 크면 진입 차단</p>
              </div>
              <Checkbox
                checked={settings.use_uptrend_4h_guard !== false}
                onCheckedChange={(checked) => updateSettings('use_uptrend_4h_guard', checked)}
              />
            </div>
            {settings.use_uptrend_4h_guard !== false && (
              <div className="grid grid-cols-2 gap-2 p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5">
                <div className="space-y-0.5">
                  <Label className="text-[10px] font-bold">4h Threshold</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.uptrend_gain_pct_4h ?? 2.5}
                    onChange={(e) => updateSettings('uptrend_gain_pct_4h', Number(e.target.value))}
                    className="h-8 text-[10px] bg-background/50"
                  />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[10px] font-bold">4h ATR x</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.uptrend_atr_multiple_4h ?? 1.5}
                    onChange={(e) => updateSettings('uptrend_atr_multiple_4h', Number(e.target.value))}
                    className="h-8 text-[10px] bg-background/50"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border border-emerald-500/30 rounded-lg bg-emerald-500/5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />
                  상위 TF ZL-MACD 방향 필터 (HTF Alignment)
                </Label>
                <p className="text-[10px] text-muted-foreground">채널 방향 + 상위 TF ZL-MACD 방향 일치 시만 진입</p>
              </div>
              <Checkbox
                checked={settings.use_htf_zl_macd_filter !== false}
                onCheckedChange={(checked) => updateSettings('use_htf_zl_macd_filter', checked)}
              />
            </div>
            {settings.use_htf_zl_macd_filter !== false && (
              <div className="grid grid-cols-4 gap-2 pt-2 border-t border-emerald-500/20">
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">상위 TF</span>
                  <Select
                    value={settings.htf_tf || '1h'}
                    onValueChange={(v) => updateSettings('htf_tf', v)}
                  >
                    <SelectTrigger className="h-7 text-[9px] font-bold bg-background/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="30m">30분</SelectItem>
                      <SelectItem value="1h">1시간</SelectItem>
                      <SelectItem value="2h">2시간</SelectItem>
                      <SelectItem value="4h">4시간</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">Fast</span>
                  <Input
                    type="number"
                    value={settings.htf_zl_macd_fast || 12}
                    onChange={(e) => updateSettings('htf_zl_macd_fast', Number(e.target.value))}
                    className="h-7 text-[9px] font-bold bg-background/50"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">Slow</span>
                  <Input
                    type="number"
                    value={settings.htf_zl_macd_slow || 26}
                    onChange={(e) => updateSettings('htf_zl_macd_slow', Number(e.target.value))}
                    className="h-7 text-[9px] font-bold bg-background/50"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[8px] font-bold text-muted-foreground">Signal</span>
                  <Input
                    type="number"
                    value={settings.htf_zl_macd_sig || 9}
                    onChange={(e) => updateSettings('htf_zl_macd_sig', Number(e.target.value))}
                    className="h-7 text-[9px] font-bold bg-background/50"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <p className="text-[11px] text-purple-400 font-bold">
              💡 Channel Trend V2 전략은 Linear Regression 채널의 하단 돌파 시 단기 EMA의 크로스 각도를 검증하여 휩쏘를 방지하고, 상위 TF ZL-MACD 방향 필터로 추세 방향까지 일치할 때만 최적의 반등 타점을 잡습니다.
            </p>
          </div>
        </div>
      )}

      {strategy_type === 'auto4_signal' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase shrink-0">최적설정</Label>
            <Select onValueChange={(v) => {
              const presets: Record<string, any> = {
                eth: { entry_mode: 'stochastic', leverage: 15, kewma_length: 45, adx_threshold: 25 },
                ena: { entry_mode: 'both', leverage: 15, kewma_length: 45, adx_threshold: 25 },
                aave: { entry_mode: 'both', leverage: 15, kewma_length: 45, adx_threshold: 25 },
                bnb: { entry_mode: 'stochastic', leverage: 15, kewma_length: 45, adx_threshold: 25 },
              };
              if (presets[v]) updateMultiSettings(presets[v]);
            }}>
              <SelectTrigger className="h-8 text-xs font-bold w-[200px] border-border rounded-lg bg-background">
                <SelectValue placeholder="종목 선택..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="eth">ETH (추천) — Stochastic</SelectItem>
                <SelectItem value="ena">ENA — Both</SelectItem>
                <SelectItem value="aave">AAVE — Both</SelectItem>
                <SelectItem value="bnb">BNB — Stochastic</SelectItem>
              </SelectContent>
            </Select>
            {['SOLUSDT', 'TURBOUSDT', 'ADAUSDT', 'BTCUSDT'].includes(symbol) && (
              <p className="text-[10px] text-amber-400 font-bold px-1">⚠ {symbol.replace('USDT', '')}은 Auto4에 부적합합니다. ETHUSDT 권장.</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">KEWMA Length</Label>
              <Input
                type="number"
                value={settings.kewma_length || 45}
                onChange={(e) => updateSettings('kewma_length', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">ADX Threshold</Label>
              <Input
                type="number"
                min="0"
                value={settings.adx_threshold ?? 25}
                onChange={(e) => updateSettings('adx_threshold', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">0 = 비활성화</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Entry Mode</Label>
              <div className="grid grid-cols-3 gap-1">
                {['stochastic', 'both', 'zlmacd'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => updateSettings('entry_mode', mode)}
                    className={`h-9 text-[10px] font-bold rounded-lg border transition-all ${(settings.entry_mode || 'stochastic') === mode ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
                  >
                    {mode === 'stochastic' ? 'Stochastic' : mode === 'both' ? 'Both' : 'ZLMACD'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">레버리지</Label>
              <Input
                type="number"
                value={settings.leverage || 15}
                onChange={(e) => updateSettings('leverage', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">TP (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.tp_pct ?? ''}
                placeholder="2.0"
                onChange={(e) => updateSettings('tp_pct', e.target.value === '' ? '' : Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">SL (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.sl_pct ?? ''}
                placeholder="1.0"
                onChange={(e) => updateSettings('sl_pct', e.target.value === '' ? '' : Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <p className="text-[11px] text-emerald-400 font-bold">
              💡 Auto4 Signal은 EMA240 장기 추세 + KEWMA(45) 중기 추세 + Stochastic(수정) 단기 필터를 3중 조합한 전략입니다. <b>4시간봉 ETH에서 검증 완료</b> (최대수익률, 최저손실률, 최고샤프비율). 느린 추세를 따라가며 과매도/과매수 구간을 포착합니다. SOL, TURBO 등 변동성 높은 종목은 부적합.
            </p>
          </div>
        </div>
      )}

      {strategy_type === 'kewma' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">KEWMA Length</Label>
              <Input
                type="number"
                value={settings.kewma_length || 45}
                onChange={(e) => updateSettings('kewma_length', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Process Noise (Q)</Label>
              <Input
                type="number"
                step="0.0001"
                value={settings.process_noise || 0.0005}
                onChange={(e) => updateSettings('process_noise', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Measure Noise (R)</Label>
              <Input
                type="number"
                step="0.001"
                value={settings.measure_noise || 0.097}
                onChange={(e) => updateSettings('measure_noise', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">ADX Threshold</Label>
              <Input
                type="number"
                min="0"
                value={settings.adx_threshold ?? 25}
                onChange={(e) => updateSettings('adx_threshold', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">0 = 비활성화</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">EWMA 근접도</Label>
              <Input
                type="number"
                step="0.0001"
                value={settings.ewma_proximity ?? 0.0015}
                onChange={(e) => updateSettings('ewma_proximity', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">가격이 EWMA와 이 범위(%×100) 내 접근 시 진입</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">레버리지</Label>
              <Input
                type="number"
                value={settings.leverage || 20}
                onChange={(e) => updateSettings('leverage', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Stop Loss (ROE %)</Label>
              <Input
                type="number"
                step="0.01"
                value={settings.sl_roe ?? -0.30}
                onChange={(e) => updateSettings('sl_roe', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">
                ROE 기준 손절 (예: -0.20 = -20%, 20x 기준 가격 1%)
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Take Profit (ROE %)</Label>
              <Input
                type="number"
                step="0.01"
                value={settings.tp_roe ?? 0.30}
                onChange={(e) => updateSettings('tp_roe', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">
                ROE 기준 익절 (예: 0.20 = +20%, 20x 기준 가격 1%)
              </p>
            </div>
          </div>
        </div>
      )}

      {(strategy_type === 'hull_macd' || strategy_type === 'zerolag_macd' || strategy_type === 'macd_ema') && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Fast Length</Label>
            <Input
              type="number"
              value={settings.fast_length || 12}
              onChange={(e) => updateSettings('fast_length', Number(e.target.value))}
              className="h-9 text-xs font-bold border-border rounded-lg bg-background"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Slow Length</Label>
            <Input
              type="number"
              value={settings.slow_length || 26}
              onChange={(e) => updateSettings('slow_length', Number(e.target.value))}
              className="h-9 text-xs font-bold border-border rounded-lg bg-background"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Signal Length</Label>
            <Input
              type="number"
              value={settings.signal_length || 9}
              onChange={(e) => updateSettings('signal_length', Number(e.target.value))}
              className="h-9 text-xs font-bold border-border rounded-lg bg-background"
            />
          </div>
        </div>
      )}

      {strategy_type === 'macd_ema' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-border/50">
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">EMA Period (주가 돌파 기준)</Label>
            <Input
              type="number"
              value={settings.ema_period || 20}
              onChange={(e) => updateSettings('ema_period', Number(e.target.value))}
              className="h-9 text-xs font-bold border-border rounded-lg bg-background"
            />
          </div>
          <div className="space-y-2 col-span-2">
            <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg h-full flex items-center">
              <p className="text-[11px] text-purple-400 font-bold">
                💡 MACD 골든크로스 발생 시 주가가 EMA 20 위에 있으면 롱 진입, 데드크로스 발생 시 주가가 EMA 20 아래에 있으면 숏 진입합니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {strategy_type === 'stochastic_ema' && (
        <div className="space-y-4 pt-2 border-t border-border/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">EMA Period (장기 필터)</Label>
              <Input
                type="number"
                value={settings.ema_period || 10}
                onChange={(e) => updateSettings('ema_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Fast Stoch %K (진입)</Label>
              <Input
                type="number"
                value={settings.fast_k_period || 5}
                onChange={(e) => updateSettings('fast_k_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Fast Stoch %D (진입)</Label>
              <Input
                type="number"
                value={settings.fast_d_period || 3}
                onChange={(e) => updateSettings('fast_d_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Slow Stoch %K (필터)</Label>
              <Input
                type="number"
                value={settings.slow_k_period || 10}
                onChange={(e) => updateSettings('slow_k_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Slow Stoch %D (필터)</Label>
              <Input
                type="number"
                value={settings.slow_d_period || 6}
                onChange={(e) => updateSettings('slow_d_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Slow Stoch Smooth</Label>
              <Input
                type="number"
                value={settings.slow_smooth_period || 6}
                onChange={(e) => updateSettings('slow_smooth_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
          <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <p className="text-[11px] text-purple-400 font-bold">
              💡 1시간봉 기준 EMA 10추세(상/하) 또는 Slow Stoch(10,6,6)의 고점/저점 과매도 구간(70/30)에서 대기하다가, Fast Stoch(5,3,3)의 데드크로스/골든크로스가 완성되는 즉시 진입하는 전략입니다.
            </p>
          </div>
        </div>
      )}

      {strategy_type === 'zerolag_macd' && (
        <div className="space-y-4 pt-2 border-t border-border/50">
          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">Signal Type (진입/청산 기준)</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => updateSettings('signal_type', 'cross')}
                className={`h-9 text-xs font-bold rounded-lg border transition-all ${(settings.signal_type || 'cross') === 'cross' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                골든/데드 크로스 (MACD & Signal)
              </button>
              <button
                type="button"
                onClick={() => updateSettings('signal_type', 'histogram')}
                className={`h-9 text-xs font-bold rounded-lg border transition-all ${settings.signal_type === 'histogram' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                히스토그램 반전 (색상 변경)
              </button>
            </div>
            <p className="text-[9px] text-muted-foreground px-1">
              💡 히스토그램 반전 모드는 MACD 선과 Signal 선이 교차하기 전에 히스토그램 막대의 증감 방향(색상)이 바뀔 때 진입하여 타점을 더 앞당깁니다.
            </p>
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">히스토그램 0선 기준 필터 (Zero Line Filter)</Label>
                <p className="text-[10px] text-muted-foreground">0선 아래서만 롱(GC), 0선 위에서만 숏(DC) 진입</p>
              </div>
              <Checkbox
                checked={settings.use_zero_line_filter !== false}
                onCheckedChange={(checked) => updateSettings('use_zero_line_filter', checked)}
              />
            </div>
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">히스토그램 진폭 필터 (Histogram Amplitude Filter)</Label>
                <p className="text-[10px] text-muted-foreground">히스토그램이 0선 근처에 밀집된 약한 신호 차단</p>
              </div>
              <Checkbox
                checked={settings.use_hist_amplitude_filter !== false}
                onCheckedChange={(checked) => updateSettings('use_hist_amplitude_filter', checked)}
              />
            </div>
            {settings.use_hist_amplitude_filter !== false && (
              <div className="space-y-2 pt-2 border-t border-border/50">
                <div className="flex justify-between items-center px-1">
                  <Label className="text-[10px] font-black text-muted-foreground uppercase">최소 진폭 (현재가 대비 %)</Label>
                  <span className="text-xs font-black text-primary">{settings.hist_amplitude_threshold_pct || 0.03}%</span>
                </div>
                <Slider
                  min={0.005}
                  max={0.2}
                  step={0.005}
                  value={[settings.hist_amplitude_threshold_pct || 0.03]}
                  onValueChange={(vals) => updateSettings('hist_amplitude_threshold_pct', vals[0])}
                />
                <p className="text-[9px] text-muted-foreground px-1 leading-relaxed">
                  💡 히스토그램 막대가 0선 근처에 모여 있을 때 발생하는 골든/데드 크로스는 ADX도 낮아 수익이 미미합니다. 이 필터는 hist 값이 설정한 % 미만이면 진입을 차단합니다.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">K선 각도 방향 필터 (K-Line Slope Filter)</Label>
                <p className="text-[10px] text-muted-foreground">GC시 K선 상승 중, DC시 K선 하락 중일 때만 진입</p>
              </div>
              <Checkbox
                checked={settings.use_k_slope_filter !== false}
                onCheckedChange={(checked) => updateSettings('use_k_slope_filter', checked)}
              />
            </div>
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">KEWMA 추세 필터 사용 (Hull + WMA)</Label>
                <p className="text-[10px] text-muted-foreground">KEWMA 하락시 숏만, 상승시 롱만 진입</p>
              </div>
              <Checkbox
                checked={settings.use_kewma_filter || false}
                onCheckedChange={(checked) => updateSettings('use_kewma_filter', checked)}
              />
            </div>

            {settings.use_kewma_filter && (
              <div className="space-y-2 pt-2 border-t border-border/50">
                <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">KEWMA Length (기간)</Label>
                <Input
                  type="number"
                  value={settings.kewma_length || 45}
                  onChange={(e) => updateSettings('kewma_length', Number(e.target.value))}
                  className="h-9 text-xs font-bold border-border rounded-lg bg-background"
                />
                <p className="text-[9px] text-muted-foreground px-1 leading-relaxed">
                  💡 KEWMA 지표가 상승 추세일 때는 롱만 진입, 하락 추세일 때는 숏만 진입하여 역추세 진입을 완벽히 차단합니다.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">EMA 120/240 저항선 익절 사용</Label>
                <p className="text-[10px] text-muted-foreground">30분봉 이상에서 EMA120/240 돌파 실패 시 조기 익절</p>
              </div>
              <Checkbox
                checked={settings.use_ema_resistance_exit || false}
                onCheckedChange={(checked) => updateSettings('use_ema_resistance_exit', checked)}
              />
            </div>
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">ADX 횡보장 진입 필터 (ADX Entry Filter)</Label>
                <p className="text-[10px] text-muted-foreground">ADX가 임계치 미만인 횡보장에서는 진입 차단</p>
              </div>
              <Checkbox
                checked={settings.use_adx_entry_filter || false}
                onCheckedChange={(checked) => updateSettings('use_adx_entry_filter', checked)}
              />
            </div>
            {settings.use_adx_entry_filter && (
              <div className="space-y-2 pt-2 border-t border-border/50">
                <div className="flex justify-between items-center px-1">
                  <Label className="text-[10px] font-black text-muted-foreground uppercase">ADX 임계치</Label>
                  <span className="text-xs font-black text-primary">{settings.adx_threshold || 20} 이상일 때만 진입</span>
                </div>
                <Slider
                  min={10}
                  max={40}
                  step={1}
                  value={[settings.adx_threshold || 20]}
                  onValueChange={(vals) => updateSettings('adx_threshold', vals[0])}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">MACD Cross Lookback (0=off)</Label>
            <Input
              type="number"
              min={0}
              max={10}
              step={1}
              value={settings.macd_cross_lookback ?? 1}
              onChange={(e) => updateSettings('macd_cross_lookback', Number(e.target.value))}
              className="h-9 text-xs font-bold border-border rounded-lg bg-background"
            />
            <p className="text-[8px] text-muted-foreground font-medium px-0.5">0: 비활성화 | 1~10: 과거 N봉 이내 cross 필터</p>
          </div>
          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">다이버전스 휩쏘 방지 필터 (Divergence Filter)</Label>
                <p className="text-[10px] text-muted-foreground">상승/하락 다이버전스 포착 시 역추세 진입 차단 (30분봉 추천)</p>
              </div>
              <Checkbox
                checked={settings.use_divergence_filter || false}
                onCheckedChange={(checked) => updateSettings('use_divergence_filter', checked)}
              />
            </div>
          </div>

          <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold">상위 타임프레임 확인 (MTF Confirmation)</Label>
                <p className="text-[10px] text-muted-foreground">상위 봉의 ZL-MACD가 수렴 중일 때만 진입 (히스토그램 반전 + 갭 임계치)</p>
              </div>
              <Checkbox
                checked={settings.use_mtf_confirmation === true}
                onCheckedChange={(checked) => updateSettings('use_mtf_confirmation', checked)}
              />
            </div>
            {settings.use_mtf_confirmation && (
              <div className="space-y-3 pt-2 border-t border-border/50">
                <div className="flex items-center justify-between px-0.5">
                  <Label className="text-[10px] font-black text-muted-foreground uppercase">상위 TF (자동)</Label>
                  <span className="text-xs font-black text-primary">{settings.interval || '15m'} → {derivedHigherInterval}</span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center px-0.5">
                    <Label className="text-[10px] font-black text-muted-foreground uppercase">갭 임계치 (%)</Label>
                    <span className="text-xs font-black text-primary">{settings.mtf_higher_gap_threshold || 0.1}%</span>
                  </div>
                  <Slider
                    min={0.02}
                    max={0.5}
                    step={0.01}
                    value={[settings.mtf_higher_gap_threshold || 0.1]}
                    onValueChange={(vals) => updateSettings('mtf_higher_gap_threshold', vals[0])}
                  />
                </div>
                <p className="text-[9px] text-muted-foreground px-0.5 leading-relaxed">
                  롱: MACD &lt; Signal &amp; Hist 상승 전환 &amp; 갭 &le; 임계치. 숏: MACD &gt; Signal &amp; Hist 하강 전환 &amp; 갭 &le; 임계치. 신호 선행/지연/과도갭 모두 차단.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {strategy_type === 'score_based_signal' && (
        <div className="space-y-4 pt-2 border-t border-border/50">
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
            <h4 className="font-black text-blue-600 text-sm mb-2">💡 Score Based Signal 전략 특징</h4>
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1 font-bold">
              <li>18개 다중 기술적 지표 및 캔들 패턴(좌측/중앙/우측 영역)에 가중치 점수를 부여합니다.</li>
              <li>롱/숏 방향별 점수 합산이 설정된 진입 임계 점수 이상일 때 타점 포착 및 진입합니다.</li>
              <li>주요 지표: 거래량(10x/5x/2x), S/R 플립 레벨, 볼린저밴드 이탈, 꼬리 성장(Wick), RSI 다이버전스, 안전 시간대 등.</li>
            </ul>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">최소 진입 임계 점수 (Min Score)</Label>
              <Input
                type="number"
                min="3"
                max="25"
                value={settings.min_entry_score || 8}
                onChange={(e) => updateSettings('min_entry_score', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">합산 점수가 이 값 이상일 때 진입 (기본: 8점)</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">레버리지</Label>
              <Input
                type="number"
                value={settings.leverage || 15}
                onChange={(e) => updateSettings('leverage', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
        </div>
      )}

      {strategy_type === 'ema_momentum_score' && (
        <div className="space-y-4">
          <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-2xl">
            <h4 className="font-black text-purple-600 text-sm mb-2">💡 EMA Momentum Score 전략 특징</h4>
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1 font-bold">
              <li>Typical Price(5중 가중) 기반 EMA16/EMA4 차이 백분율(diff)로 7가지 추세 요소 점수(0~12) 계산</li>
              <li>스코어가 진입 임계값(기본 9) 이상 상향 돌파 + 과거 8봉 내 저점(5) 이하였어야 진입</li>
              <li>저빈도(월 4~8회) 고승률 전략, SL 1~1.5% / TP 2% 고정</li>
              <li>급등 시 EMA16 대비 가격 이격도(기본 3%) 초과 시 고점 추격 방지</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">진입 스코어 기준</Label>
              <Input
                type="number"
                min="1"
                max="12"
                value={settings.score_entry_threshold ?? 9}
                onChange={(e) => updateSettings('score_entry_threshold', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">높을수록 진입 적음 (기본 9)</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">청산 스코어 기준</Label>
              <Input
                type="number"
                min="0"
                max="11"
                value={settings.score_exit_threshold ?? 5}
                onChange={(e) => updateSettings('score_exit_threshold', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">이하로 떨어지면 청산 (기본 5)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">저점 기준 스코어</Label>
              <Input
                type="number"
                min="0"
                max="10"
                value={settings.score_low_zone ?? 5}
                onChange={(e) => updateSettings('score_low_zone', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">저점 확인 기준 (기본 5)</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">저점 탐색 봉 수</Label>
              <Input
                type="number"
                min="3"
                max="20"
                value={settings.score_lookback ?? 8}
                onChange={(e) => updateSettings('score_lookback', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">과거 탐색 범위 (기본 8)</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">가격 급등 필터(%)</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={settings.max_price_deviation_pct ?? 3}
                onChange={(e) => updateSettings('max_price_deviation_pct', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">EMA16 대비 이격도 제한, 0=비활성</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">EMA 장기(16)</Label>
              <Input
                type="number"
                min="5"
                max="50"
                value={settings.patrick_ema_len1 ?? 16}
                onChange={(e) => updateSettings('patrick_ema_len1', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">EMA 단기(4)</Label>
              <Input
                type="number"
                min="2"
                max="20"
                value={settings.patrick_ema_len2 ?? 4}
                onChange={(e) => updateSettings('patrick_ema_len2', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">트레일러 기간</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={settings.patrick_trailer_len ?? 3}
                onChange={(e) => updateSettings('patrick_trailer_len', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">SL (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.sl_pct ?? 1.0}
                onChange={(e) => updateSettings('sl_pct', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">TP (%)</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.tp_pct ?? 2.0}
                onChange={(e) => updateSettings('tp_pct', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">스코어 하한 청산</Label>
              <Input
                type="number"
                min="0"
                max="11"
                value={settings.score_floor_exit ?? 7}
                onChange={(e) => updateSettings('score_floor_exit', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">스코어 이하 시 청산</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">급락 청산(Point)</Label>
              <Input
                type="number"
                min="0"
                max="10"
                value={settings.score_drawdown_exit ?? 3}
                onChange={(e) => updateSettings('score_drawdown_exit', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
              <p className="text-[9px] text-muted-foreground px-1">최고점 대비 하락 시</p>
            </div>
          </div>
        </div>
      )}

      {strategy_type === 'auto7_signal' && (
        <div className="space-y-4">
          <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-2xl">
            <h4 className="font-black text-purple-600 text-sm mb-2">💡 Auto7 Signal 전략 특징</h4>
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1 font-bold">
              <li>1시간봉 OB 구역(Swing High/Low 16) + Stochastic 과매수 확인</li>
              <li>Volume Mountain 감지: 연속 음봉 3개 + 중간 거래량 높음 (뫼산 패턴)</li>
              <li>Volume Mountain + OB 저항 근접 + Stochastic ≥ 80 시 숏 진입</li>
              <li>손절: 뫼산 꼭대기(3봉 최고가) 또는 ATR×2.0 중 높은 쪽</li>
              <li>백테스트 결과: ETH +2.11%, ENA +5.16%, NEAR +2.81%, SOL +3.18% (숏 전용)</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">롱(Long) 진입 활성화</Label>
                  <p className="text-[10px] text-muted-foreground">비활성화 시 숏(Short) 진입만 동작</p>
                </div>
                <Checkbox
                  checked={settings.enable_long === true}
                  onCheckedChange={(checked) => updateSettings('enable_long', checked)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">레버리지</Label>
              <Input
                type="number"
                value={settings.leverage || 20}
                onChange={(e) => updateSettings('leverage', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
        </div>
      )}

      {strategy_type === 'perfect_alignment_pullback' && (
        <div className="space-y-4">
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
            <h4 className="font-black text-emerald-600 text-sm mb-2">💡 Perfect Alignment Pullback 전략 특징</h4>
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1 font-bold">
              <li>EMA 60 &gt; 89 &gt; 120 정배열 롱 진입 / 역배열 숏 진입 (눌림목 타점)</li>
              <li>청산: 롱은 EMA 20이 EMA 60 데드크로스, 숏은 골든크로스 시 청산</li>
              <li>매크로 추세 필터: EMA 240 지표를 기반으로 전체 시장 추세 방향과 일치하는 주문만 진입</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">매크로 추세 필터 사용</Label>
                  <p className="text-[10px] text-muted-foreground">EMA 240 방향과 일치하는 진입만 허용 (상승장/하락장 구분)</p>
                </div>
                <Checkbox
                  checked={settings.use_macro_trend_filter !== false}
                  onCheckedChange={(checked) => updateSettings('use_macro_trend_filter', checked)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">매크로 EMA 기간 (Macro Period)</Label>
              <Input
                type="number"
                value={settings.macro_trend_period || 240}
                onChange={(e) => updateSettings('macro_trend_period', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col p-3 border border-border rounded-lg bg-background/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-xs font-bold">분할 익절 사용 (Partial TP)</Label>
                  <p className="text-[10px] text-muted-foreground">목표가 도달 시 순차적 분할 익절 적용</p>
                </div>
                <Checkbox
                  checked={settings.use_partial_tp || false}
                  onCheckedChange={(checked) => updateSettings('use_partial_tp', checked)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black text-muted-foreground uppercase px-1">레버리지</Label>
              <Input
                type="number"
                value={settings.leverage || 20}
                onChange={(e) => updateSettings('leverage', Number(e.target.value))}
                className="h-9 text-xs font-bold border-border rounded-lg bg-background"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
